import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Minimize2, Maximize2, Sparkles, RefreshCw, ArrowDown, Bot, User, MoreVertical, Trash2, Copy, Check } from 'lucide-react';
import { Message } from '../hooks/useChatGPT';
interface OracleChatWidgetProps {
  messages: Message[];
  sendMessage: (content: string) => Promise<void>;
  isLoading: boolean;
  onClearHistory: () => void;
}
export function OracleChatWidget({
  messages,
  sendMessage,
  isLoading,
  onClearHistory
}: OracleChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  // Scroll to bottom when messages change
  useEffect(() => {
    if (isOpen && !isMinimized && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [messages, isOpen, isMinimized, isLoading]);
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    const msg = inputValue;
    setInputValue('');
    await sendMessage(msg);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setShowCopyToast(true);
    setTimeout(() => setShowCopyToast(false), 2000);
  };
  // Simple markdown stripper for preview
  const stripMarkdown = (text: string) => {
    return text.replace(/\*\*(.+?)\*\*/g, '$1').replace(/__(.+?)__/g, '$1').replace(/\*(.+?)\*/g, '$1').replace(/_(.+?)_/g, '$1').replace(/`(.+?)`/g, '$1').replace(/\[(.+?)\]\(.+?\)/g, '$1');
  };
  // Render message content with basic formatting
  const renderMessage = (content: string) => {
    return content.split('\n').map((line, i) => <p key={i} className={`min-h-[1.2em] ${line.startsWith('-') || line.startsWith('•') ? 'pl-4' : ''}`} data-id="element-490">
        {line}
      </p>);
  };
  return <>
      {/* Minimized / Closed State - Floating Button */}
      <AnimatePresence data-id="element-491">
        {!isOpen && <motion.button initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0,
        opacity: 0
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-lg shadow-pink-500/30 flex items-center justify-center hover:shadow-pink-500/50 transition-shadow" data-id="element-492">
            <Sparkles size={24} data-id="element-493" />
            {messages.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center border-2 border-white" data-id="element-494">
                {messages.length}
              </span>}
          </motion.button>}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence data-id="element-495">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 100,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: isMinimized ? 0 : 0,
        scale: 1,
        height: isMinimized ? 'auto' : '60vh',
        width: isMinimized ? '300px' : '400px'
      }} exit={{
        opacity: 0,
        y: 100,
        scale: 0.9
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className={`fixed bottom-0 right-6 z-50 bg-white rounded-t-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden ${isMinimized ? 'mb-0' : 'mb-0'}`} style={{
        maxHeight: '80vh'
      }} data-id="element-496">
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex items-center justify-between cursor-pointer" onClick={() => setIsMinimized(!isMinimized)} data-id="element-497">
              <div className="flex items-center gap-2" data-id="element-498">
                <div className="p-1.5 bg-white/10 rounded-lg" data-id="element-499">
                  <Sparkles size={16} className="text-pink-400" data-id="element-500" />
                </div>
                <div data-id="element-501">
                  <h3 className="text-sm font-bold" data-id="element-502">Jua Oracle</h3>
                  <p className="text-[10px] text-slate-400 flex items-center gap-1" data-id="element-503">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" data-id="element-504" />
                    Online
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1" data-id="element-505">
                <button onClick={e => {
              e.stopPropagation();
              setIsMinimized(!isMinimized);
            }} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-id="element-506">
                  {isMinimized ? <Maximize2 size={14} data-id="element-507" /> : <Minimize2 size={14} data-id="element-508" />}
                </button>
                <button onClick={e => {
              e.stopPropagation();
              setIsOpen(false);
            }} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors hover:text-red-400" data-id="element-509">
                  <X size={14} data-id="element-510" />
                </button>
              </div>
            </div>

            {/* Body - Only visible when not minimized */}
            {!isMinimized && <>
                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50 custom-scrollbar" data-id="element-511">
                  {messages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center p-6 opacity-60" data-id="element-512">
                      <div className="w-16 h-16 bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl flex items-center justify-center mb-4" data-id="element-513">
                        <Bot size={32} className="text-pink-500" data-id="element-514" />
                      </div>
                      <h4 className="font-semibold text-slate-900 mb-1" data-id="element-515">
                        How can I help?
                      </h4>
                      <p className="text-xs text-slate-500 max-w-[200px]" data-id="element-516">
                        Ask about market trends, weather impact, or trading
                        strategies.
                      </p>
                    </div> : messages.map((msg, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-517">
                        <div className={`max-w-[85%] rounded-2xl p-3 text-sm shadow-sm relative group ${msg.role === 'user' ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white rounded-tr-sm' : 'bg-white border border-slate-200 text-slate-700 rounded-tl-sm'}`} data-id="element-518">
                          <div className="whitespace-pre-wrap leading-relaxed" data-id="element-519">
                            {renderMessage(msg.content)}
                          </div>

                          {/* Message Actions */}
                          <div className={`absolute top-2 ${msg.role === 'user' ? '-left-8' : '-right-8'} opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1`} data-id="element-520">
                            <button onClick={() => copyToClipboard(msg.content)} className="p-1.5 bg-white border border-slate-200 rounded-full shadow-sm hover:bg-slate-50 text-slate-500" title="Copy" data-id="element-521">
                              <Copy size={10} data-id="element-522" />
                            </button>
                          </div>
                        </div>
                      </motion.div>)}

                  {isLoading && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex justify-start" data-id="element-523">
                      <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm p-3 shadow-sm flex items-center gap-2" data-id="element-524">
                        <RefreshCw size={14} className="text-pink-500 animate-spin" data-id="element-525" />
                        <span className="text-xs text-slate-500 font-medium" data-id="element-526">
                          Thinking...
                        </span>
                      </div>
                    </motion.div>}

                  <div ref={messagesEndRef} data-id="element-527" />
                </div>

                {/* Input Area */}
                <div className="p-3 bg-white border-t border-slate-100" data-id="element-528">
                  <div className="relative flex items-end gap-2" data-id="element-529">
                    <div className="relative flex-1" data-id="element-530">
                      <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask a question..." className="w-full pl-4 pr-10 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all resize-none max-h-[100px] min-h-[44px] scrollbar-hide" rows={1} style={{
                  height: '44px'
                }} data-id="element-531" />
                    </div>
                    <button onClick={handleSend} disabled={!inputValue.trim() || isLoading} className="p-3 bg-pink-500 text-white rounded-xl hover:bg-pink-600 disabled:opacity-50 disabled:hover:bg-pink-500 transition-colors shadow-sm shadow-pink-500/20 flex-shrink-0" data-id="element-532">
                      <Send size={18} data-id="element-533" />
                    </button>
                  </div>

                  <div className="flex justify-between items-center mt-2 px-1" data-id="element-534">
                    <p className="text-[10px] text-slate-400" data-id="element-535">
                      AI can make mistakes. Verify important info.
                    </p>
                    {messages.length > 0 && <button onClick={onClearHistory} className="text-[10px] text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors" data-id="element-536">
                        <Trash2 size={10} data-id="element-537" />
                        Clear chat
                      </button>}
                  </div>
                </div>
              </>}

            {/* Minimized Preview (Optional - if we want a bar instead of just header) */}
            {isMinimized && <div className="px-4 py-2 bg-white border-t border-slate-100 text-xs text-slate-500 truncate cursor-pointer" onClick={() => setIsMinimized(false)} data-id="element-538">
                {messages.length > 0 ? stripMarkdown(messages[messages.length - 1].content) : 'Click to expand chat...'}
              </div>}
          </motion.div>}
      </AnimatePresence>

      {/* Copy Toast */}
      <AnimatePresence data-id="element-539">
        {showCopyToast && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="fixed bottom-24 right-1/2 translate-x-1/2 z-[60] bg-slate-900 text-white px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2 text-xs font-medium" data-id="element-540">
            <Check size={12} className="text-green-400" data-id="element-541" />
            Copied to clipboard
          </motion.div>}
      </AnimatePresence>
    </>;
}